'use client';
import clsx from 'clsx';
import React, {
  ChangeEvent, useCallback, useEffect, useRef, useState
} from 'react';
import { AiFillCheckCircle } from 'react-icons/ai';
import { BiSolidCloudUpload } from 'react-icons/bi';
import { GoAlertFill } from 'react-icons/go';
import { MdCancel } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/button/button';
import { Loader } from '@/components/loader/loader';
import Modal from '@/components/modal/modal';
import { Heading } from '@/components/typography/heading';
import { useUpdateContract, useValidateContract } from '@/hooks/queries/useDataContracts';
import { ValidationState } from '@/types/dataContracts';

import styles from './UpdateContractModal.module.scss';

type UpdateContractModalProps = {
  contractId: string;
  contractName: string;
  currentYaml: string;
  onClose: () => void;
  onUpdated?: () => void;
};

type UpdateResult = 'none' | 'success' | 'breaking_changes';

export default function UpdateContractModal( {
  contractId,
  contractName,
  currentYaml,
  onClose,
  onUpdated
}: UpdateContractModalProps ) {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>( null );

  const [ yamlContent, setYamlContent ] = useState<string>( currentYaml );
  const [ validatedYaml, setValidatedYaml ] = useState<string>( currentYaml );
  const [ validationState, setValidationState ] = useState<ValidationState>( ValidationState.NONE );
  const [ validationMessage, setValidationMessage ] = useState<string>( '' );
  const [ uploadedFileName, setUploadedFileName ] = useState<string>( '' );
  const [ hasChanges, setHasChanges ] = useState( false );
  const [ updateResult, setUpdateResult ] = useState<UpdateResult>( 'none' );
  const [ updateErrorMessage, setUpdateErrorMessage ] = useState( '' );

  const validateMutation = useValidateContract();
  const updateMutation = useUpdateContract();

  // Track whether content has changed from original
  useEffect( () => {
    setHasChanges( yamlContent !== currentYaml );
  }, [ yamlContent, currentYaml ] );

  // Auto-validate uploaded file
  const handleFileUpload = useCallback( async( e: React.ChangeEvent<HTMLInputElement> ) => {
    const file = e.target.files?.[0];
    if ( !file ) return;

    const isYaml = file.name.endsWith( '.yaml' ) || file.name.endsWith( '.yml' );
    if ( !isYaml ) return;

    setUploadedFileName( file.name );
    setValidationState( ValidationState.VALIDATING );
    setValidationMessage( '' );

    const content = await file.text();
    setYamlContent( content );

    try {
      const result = await validateMutation.mutateAsync( file );
      setValidatedYaml( content );
      if ( result.is_valid ) {
        setValidationState( ValidationState.VALID );
        setValidationMessage( result.message || 'Contract is valid' );
      } else {
        setValidationState( ValidationState.INVALID );
        setValidationMessage( result.message || 'Contract validation failed' );
      }
    } catch( err ) {
      setValidationState( ValidationState.INVALID );
      setValidationMessage( err instanceof Error ? err.message : 'Validation error' );
    }

    // Reset so the same file can be re-selected
    if ( fileInputRef.current ) {
      fileInputRef.current.value = '';
    }
  }, [ validateMutation ] );

  // Detect YAML edits
  const handleYamlChange = ( e: ChangeEvent<HTMLTextAreaElement> ) => {
    const newContent = e.target.value;
    setYamlContent( newContent );

    if ( validationState === ValidationState.VALID || validationState === ValidationState.INVALID ) {
      if ( newContent !== validatedYaml ) {
        setValidationState( ValidationState.MODIFIED );
      } else {
        setValidationState( validateMutation.data?.is_valid ? ValidationState.VALID : ValidationState.INVALID );
      }
    } else if ( validationState === ValidationState.NONE && newContent !== currentYaml ) {
      setValidationState( ValidationState.MODIFIED );
    }
  };

  // Re-validate after edit
  const handleValidate = useCallback( async() => {
    if ( !yamlContent ) return;

    setValidationState( ValidationState.VALIDATING );
    setValidationMessage( '' );

    const editedFile = new File( [ yamlContent ], 'contract.yaml', { type: 'application/x-yaml' } );

    try {
      const result = await validateMutation.mutateAsync( editedFile );
      setValidatedYaml( yamlContent );
      if ( result.is_valid ) {
        setValidationState( ValidationState.VALID );
        setValidationMessage( result.message || 'Contract is valid' );
      } else {
        setValidationState( ValidationState.INVALID );
        setValidationMessage( result.message || 'Contract validation failed' );
      }
    } catch( err ) {
      setValidationState( ValidationState.INVALID );
      setValidationMessage( err instanceof Error ? err.message : 'Validation error' );
    }
  }, [ yamlContent, validateMutation ] );

  // Update contract
  const handleUpdateContract = useCallback( async() => {
    if ( validationState !== ValidationState.VALID || !hasChanges ) return;

    const file = new File( [ yamlContent ], 'contract.yaml', { type: 'application/x-yaml' } );

    try {
      await updateMutation.mutateAsync( { contractId, file } );
      setUpdateResult( 'success' );
      onUpdated?.();
    } catch( err ) {
      const message = err instanceof Error ? err.message : 'Update failed';
      setUpdateResult( 'breaking_changes' );
      setUpdateErrorMessage( message );
    }
  }, [ validationState, hasChanges, yamlContent, contractId, updateMutation, onUpdated ] );

  const handleCreateNewContract = useCallback( () => {
    onClose();
    navigate( '/dataCatalog/create' );
  }, [ navigate, onClose ] );

  const isUpdateDisabled = validationState !== ValidationState.VALID || !hasChanges || updateMutation.isPending;
  const showValidateButton = validationState === ValidationState.MODIFIED || validationState === ValidationState.INVALID;

  const renderValidationStatus = () => {
    switch ( validationState ) {
      case ValidationState.VALIDATING:
        return (
          <div className={ clsx( styles.validationStatus, styles.validationValidating ) }>
            <Loader /> Validating...
          </div>
        );
      case ValidationState.VALID:
        return (
          <div className={ clsx( styles.validationStatus, styles.validationValid ) }>
            <AiFillCheckCircle /> { validationMessage || 'Valid' }
          </div>
        );
      case ValidationState.INVALID:
        return (
          <div className={ clsx( styles.validationStatus, styles.validationInvalid ) }>
            <MdCancel /> { validationMessage || 'Invalid' }
          </div>
        );
      case ValidationState.MODIFIED:
        return (
          <div className={ clsx( styles.validationStatus, styles.validationModified ) }>
            <GoAlertFill /> YAML modified — please validate your changes
          </div>
        );
      default:
        return null;
    }
  };

  // Result screens
  if ( updateResult === 'success' ) {
    return (
      <Modal className={ styles.updateModal } resizable modalType="update-contract" initialSize="medium">
        <div className={ styles.modalInner }>
          <div className={ styles.header }>
            <Heading as="h2" size="md">Update Contract</Heading>
          </div>
          <div className={ styles.resultOverlay }>
            <AiFillCheckCircle className={ clsx( styles.resultIcon, styles.resultIconSuccess ) } />
            <p className={ styles.resultMessage }>Contract Update Successful</p>
            <p className={ styles.resultSubMessage }>
              <strong>{ contractName }</strong> has been updated.
            </p>
          </div>
          <div className={ styles.footer }>
            <Button variant="secondary" size="sm" onClick={ onClose }>
              Close
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  if ( updateResult === 'breaking_changes' ) {
    return (
      <Modal className={ styles.updateModal } resizable modalType="update-contract" initialSize="medium">
        <div className={ styles.modalInner }>
          <div className={ styles.header }>
            <Heading as="h2" size="md">Update Contract</Heading>
          </div>
          <div className={ styles.resultOverlay }>
            <MdCancel className={ clsx( styles.resultIcon, styles.resultIconFailed ) } />
            <p className={ styles.resultMessage }>Contract Update Failed</p>
            <p className={ styles.resultSubMessage }>
              { updateErrorMessage || 'Breaking changes detected. Please create a new contract instead.' }
            </p>
          </div>
          <div className={ styles.footer }>
            <Button variant="secondary" size="sm" onClick={ onClose }>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={ handleCreateNewContract }>
              Create New Contract
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal className={ styles.updateModal } resizable modalType="update-contract" initialSize="large">
      <div className={ styles.modalInner }>
        <div className={ styles.header }>
          <Heading as="h2" size="md">Update Contract</Heading>
          <p className={ styles.contractName }>{ contractName }</p>
        </div>

        <div className={ styles.content }>
          { /* Upload section */ }
          <div className={ styles.uploadSection }>
            <label>Upload a new YAML file</label>
            <div className={ styles.uploadRow }>
              <Button
                variant="secondary"
                size="sm"
                onClick={ () => fileInputRef.current?.click() }
                icon={ <BiSolidCloudUpload /> }
              >
                Upload YAML
              </Button>
              { uploadedFileName && (
                <span className={ styles.uploadedFileName }>{ uploadedFileName }</span>
              ) }
              <input
                ref={ fileInputRef }
                type="file"
                accept=".yaml,.yml"
                onChange={ handleFileUpload }
                style={ { display: 'none' } }
              />
            </div>
          </div>

          { /* Validation status */ }
          { validationState !== ValidationState.NONE && renderValidationStatus() }

          { /* YAML editor */ }
          <textarea
            className={ styles.yamlEditor }
            value={ yamlContent }
            onChange={ handleYamlChange }
            spellCheck={ false }
            data-testid="update-yaml-editor"
          />
        </div>

        <div className={ styles.footer }>
          <Button variant="secondary" size="sm" onClick={ onClose }>
            Cancel
          </Button>
          { showValidateButton && (
            <Button
              variant="secondary"
              size="sm"
              onClick={ handleValidate }
              loading={ validateMutation.isPending }
            >
              Validate
            </Button>
          ) }
          <Button
            variant="primary"
            size="sm"
            onClick={ handleUpdateContract }
            disabled={ isUpdateDisabled }
            loading={ updateMutation.isPending }
          >
            Update Contract
          </Button>
        </div>
      </div>
    </Modal>
  );
}
